import styled from '@emotion/styled';
import { tokens } from '@hotjar/hotsauce';
import {
  RegexNotSupportedError,
  HeatmapGenerationError as HeatmapGenerationErrorComponent,
  NoDataFoundError,
  WaitLongerError,
  HEATMAP_DATA_TYPES,
  HEATMAP_GENERATION_FAILURE_ERRORS,
  NoDataCheckTrackingError,
  NoDataIncreaseCoverageError,
  SMART_PANEL_EXPANDED_WIDTH,
  CONTENT_RIGHT_LEFT_PADDING,
  SIDEBAR_WIDTH as HEATMAPS_SIDEBAR_WIDTH,
  TopClicksLayer,
  MAGIC_RULER_WIDTH,
} from '@hotjar/heatmaps';
import { css } from '@emotion/react';
import { SIDEBAR_COLLAPSED_WIDTH } from '@hotjar/navigation';
import { ScrollMarkersLayer } from './ScrollMarkers';
import { ClickRegions } from '../ClickRegions/ClickRegions';
import type { RenderClickRegionsParams } from './ReportTypes';

export const renderClickRegions = ({
  heatmapData,
  screenshotData,
  selectedDeviceType,
  onRegionClick,
}: RenderClickRegionsParams): JSX.Element | null => {
  if (
    heatmapData.type !== HEATMAP_DATA_TYPES.CLICK ||
    heatmapData.clickCount <= 0 ||
    heatmapData.clickRegions.length <= 0 ||
    !screenshotData.imageSize
  ) {
    return null;
  }

  return (
    <ClickRegions
      clickRegions={heatmapData.clickRegions}
      selectedDeviceType={selectedDeviceType}
      count={heatmapData.clickCount}
      width={screenshotData.imageSize[0]}
      height={screenshotData.imageSize[1]}
      onRegionClick={onRegionClick}
      topClickedElements={heatmapData.topClickedElements}
    />
  );
};

export const renderScrollMarkers = (
  heatmapData: HeatmapAPI.GetHeatDataResponse<HeatmapDataType>,
): JSX.Element | false => {
  return (
    heatmapData.type === HEATMAP_DATA_TYPES.SCROLL && (
      <ScrollMarkersLayer heatmapData={heatmapData} />
    )
  );
};

export const renderTopClicks = (
  heatmapData: HeatmapAPI.GetHeatDataResponse<HeatmapDataType>,
  screenshotData: {
    imageSize: [number, number] | null;
  },
  selectedDeviceType: HeatmapDeviceTypes,
): JSX.Element | null => {
  if (
    heatmapData.type !== HEATMAP_DATA_TYPES.CLICK ||
    !screenshotData.imageSize ||
    !heatmapData.topClickedElements ||
    heatmapData.topClickedElements.length < 0 ||
    heatmapData.clickRegions.length < 0
  ) {
    return null;
  }

  return (
    <TopClicksLayer
      topClickedElements={heatmapData.topClickedElements}
      selectedDeviceType={selectedDeviceType}
      width={screenshotData.imageSize[0]}
      height={screenshotData.imageSize[1]}
      clickRegionsCount={heatmapData.clickRegions.length}
    />
  );
};

export const renderErrorMessage = ({
  siteId,
  errorType,
  onRetry,
  onNavigateToHeatmap,
  onUpgradeCoverage,
  shouldOpenManualRetaker,
}: {
  siteId: number;
  errorType?: HeatmapGenerationError;
  onRetry: () => void;
  onNavigateToHeatmap: (url: string) => void;
  onUpgradeCoverage: () => void;
  shouldOpenManualRetaker?: boolean;
}): JSX.Element => {
  switch (errorType) {
    case HEATMAP_GENERATION_FAILURE_ERRORS.TRACKING_CODE_INSTALLED_NO_SESSIONS:
      return <WaitLongerError siteId={siteId} showSuggestions={false} />;
    case HEATMAP_GENERATION_FAILURE_ERRORS.NO_SESSIONS_FOR_URL:
      return (
        <WaitLongerError
          siteId={siteId}
          showSuggestions
          onNavigateToHeatmap={onNavigateToHeatmap}
        />
      );
    case HEATMAP_GENERATION_FAILURE_ERRORS.NO_HEATMAP_DATA_FOUND:
      return <NoDataFoundError />;
    case HEATMAP_GENERATION_FAILURE_ERRORS.REGEX_MATCH_NOT_SUPPORTED:
      return <RegexNotSupportedError />;
    case HEATMAP_GENERATION_FAILURE_ERRORS.NO_HEATMAP_DATA_CHECK_TRACKING:
      return <NoDataCheckTrackingError />;
    case HEATMAP_GENERATION_FAILURE_ERRORS.NO_HEATMAP_DATA_INCREASE_COVERAGE:
      return <NoDataIncreaseCoverageError onUpgrade={onUpgradeCoverage} />;
    default:
      return (
        <HeatmapGenerationErrorComponent
          shouldOpenManualRetaker={shouldOpenManualRetaker}
          onRetry={onRetry}
        />
      );
  }
};

export const ReportContainer = styled.div`
  background-color: ${tokens.color.ui_background};
  min-height: 420px;
  display: flex;
  flex-direction: column;
  isolation: isolate;
  position: relative;
  /* Negative margin to offset/counter the padding of the main parent container */
  margin-left: -${CONTENT_RIGHT_LEFT_PADDING};
`;

export const MagicRulerAndLayersContainer = styled.div`
  display: grid;
  width: 100%;
  grid-template-columns: ${MAGIC_RULER_WIDTH} 1fr;
  border-bottom: 1px solid ${tokens.color.ui_border_light};
`;

export const MagicRulerContainer = styled.div`
  width: ${MAGIC_RULER_WIDTH};
`;

export const LayersContainer = styled.div<{
  fullWidth?: boolean;
  isSmartPanelEnabled?: boolean;
  isSmartPanelOpen?: boolean;
}>`
  position: relative;
  display: grid;
  justify-content: ${({ fullWidth }) => (fullWidth ? 'stretch' : 'center')};
  overflow: hidden;
  ${({ isSmartPanelEnabled, isSmartPanelOpen }) =>
    isSmartPanelEnabled &&
    css`
      max-width: calc(100% - ${SMART_PANEL_EXPANDED_WIDTH}px + (${CONTENT_RIGHT_LEFT_PADDING}));
      min-width: calc(
        1280px - ${SIDEBAR_COLLAPSED_WIDTH}px - ${HEATMAPS_SIDEBAR_WIDTH}px -
          ${SMART_PANEL_EXPANDED_WIDTH}px
      );
      margin: ${!isSmartPanelOpen ? '0 auto' : 'initial'};
    `}
`;
